import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Users, Target, ArrowRight } from 'lucide-react';
import { FrameworksSection } from '@/components/change-management/FrameworksSection';
import { RoleRedesignSection } from '@/components/change-management/RoleRedesignSection';
import { OrganizationFilter } from '@/components/admin/OrganizationFilter';
import { useChangeManagementSummary } from '@/hooks/useChangeManagement';
import { useAuth } from '@/contexts/AuthContext';

export default function ChangeManagementPage() {
  const { profile, roles, currentRole, isLoading: authLoading } = useAuth();
  const isSuperAdmin = currentRole === 'super_admin';
  const isAdmin = currentRole === 'admin' || isSuperAdmin;
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [selectedOrg, setSelectedOrg] = useState<string>('all');
  const [refreshKey, setRefreshKey] = useState(0);

  // Para admins no-super, usar directamente su organización
  const organizationId = isSuperAdmin
    ? (selectedOrg === 'all' ? null : selectedOrg)
    : (profile?.organization_id || null);

  const { frameworkProgress, rolesCount, rolesRedesigned, loading } = useChangeManagementSummary(organizationId, refreshKey);

  const handleBack = () => {
    setRefreshKey(prev => prev + 1);
    setActiveSection(null);
  };

  const sections = [
    {
      id: 'frameworks',
      title: 'Plan de Gestión del Cambio',
      description: 'Kotter + ADKAR: Checklist de tareas',
      icon: Target,
      color: 'from-blue-500 to-cyan-600',
      progress: frameworkProgress,
      subtitle: `${frameworkProgress}% completado`,
    },
    {
      id: 'roles',
      title: 'Rediseño de Roles',
      description: 'Colaboración Human-AI',
      icon: Users,
      color: 'from-emerald-500 to-teal-600',
      progress: rolesCount > 0 ? Math.round((rolesRedesigned / rolesCount) * 100) : null,
      subtitle: rolesCount > 0 ? `${rolesRedesigned}/${rolesCount} rediseñados` : '0 roles definidos',
    }
  ];

  if (authLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (activeSection) {
    switch (activeSection) {
      case 'frameworks':
        return <FrameworksSection onBack={handleBack} organizationId={organizationId} />;
      case 'roles':
        return <RoleRedesignSection onBack={handleBack} organizationId={organizationId} />;
      default:
        return null;
    }
  }

  if (!isSuperAdmin && !organizationId) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <p className="text-muted-foreground">No tienes una organización asociada. Contacta al administrador.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {isSuperAdmin && (
        <div className="flex justify-end">
          <OrganizationFilter selectedOrg={selectedOrg} onOrgChange={setSelectedOrg} />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {sections.map((section) => {
          const Icon = section.icon;
          return (
            <Card 
              key={section.id}
              className="group cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1 border-border/50 overflow-hidden"
              onClick={() => setActiveSection(section.id)}
            >
              <div className={`h-2 bg-gradient-to-r ${section.color}`} />
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${section.color} shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  {loading ? (
                    <Skeleton className="h-5 w-24" />
                  ) : (
                    <Badge variant="outline" className="text-xs">{section.subtitle}</Badge>
                  )}
                </div>
                <CardTitle className="text-lg mt-4 group-hover:text-primary transition-colors">
                  {section.title}
                </CardTitle>
                <CardDescription>{section.description}</CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                {section.progress !== null && (
                  <Progress value={section.progress} className="h-2 mb-4" />
                )}
                <Button 
                  variant="ghost" 
                  className="w-full justify-between group-hover:bg-primary/10"
                >
                  <span>Explorar</span>
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
